import React, { useState } from 'react'
import { IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@material-ui/core'
import {
    // Semantic rename icons according to application action
    KeyboardReturn as FetchInvoiceIcon,
    Archive as DownloadInvoiceIcon,
    Build as ProcessInvoiceIcon,
    Edit as EditFilesIcon,
} from '@material-ui/icons'
import { useAxiosClient } from '../../../contexts/Axios'
import { BaseRowActionButtonCreator } from '../../../components/BPMTable/TableMapperCreator'
import FileInput from '../../../components/Inputs/FileInput'


const FetchFilesButton = ({ rowData, ctx }) => {
    const { axiosClient } = useAxiosClient()
    const [loading, setLoading] = useState(false)

    const handleClick = (event) => {
        event.stopPropagation()
        setLoading(true)
        axiosClient.post(`/billing/available-invoices/${rowData.pk}/fetch-files/`)
            .then(() => ctx.refreshTable())
            .catch(error => console.error('Fetch files error:', error))
            .finally(() => setLoading(false))
    }

    if (loading) return <CircularProgress size={20} />

    return (
        <IconButton size="small" onClick={handleClick} title="Fetch Files">
            <FetchInvoiceIcon />
        </IconButton>
    )
}


const ProcessButton = ({ rowData, ctx }) => {
    const { axiosClient } = useAxiosClient()
    const [loading, setLoading] = useState(false)

    const handleClick = (event) => {
        event.stopPropagation()
        setLoading(true)
        axiosClient.post(`/billing/available-invoices/${rowData.pk}/process/`)
            .then(() => ctx.refreshTable())
            .catch(error => console.error('Process error:', error))
            .finally(() => setLoading(false))
    }

    if (loading) return <CircularProgress size={20} />

    return (
        <IconButton size="small" onClick={handleClick} title="Process">
            <ProcessInvoiceIcon />
        </IconButton>
    )
}


const DownloadFilesButton = ({ rowData }) => {
    const { axiosClient } = useAxiosClient()
    const [loading, setLoading] = useState(false)

    const handleClick = async (event) => {
        event.stopPropagation()
        setLoading(true)
        try {
            // Prefer BillingRequestFile entries stored in bpm-api when available
            let filesToDownload = []
            try {
                const resp = await axiosClient.get(`/billing/available-invoices/${rowData.pk}/request-files/`)
                filesToDownload = resp.data.files || []
            } catch (_) {
                // endpoint unavailable — fall through to raw crawler files
            }

            // Fall back to the original crawler files stored in AvailableInvoice.files
            if (filesToDownload.length === 0) {
                const raw = rowData.files
                filesToDownload = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : []
            }

            for (const file of filesToDownload) {
                const response = await axiosClient.get(file.download_url, { responseType: 'blob' })
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', file.name)
                document.body.appendChild(link)
                link.click()
                link.parentNode.removeChild(link)
                window.URL.revokeObjectURL(url)
            }
        } catch (error) {
            console.error('Download files error:', error)
        } finally {
            setLoading(false)
        }
    }

    if (loading) return <CircularProgress size={20} />

    return (
        <IconButton size="small" onClick={handleClick} title="Download Files">
            <DownloadInvoiceIcon />
        </IconButton>
    )
}


class FetchFilesRowActionButtonCreator extends BaseRowActionButtonCreator {
    constructor() {
        super()
        this.actionId = 'fetchFiles'
    }
    createWith = (rowData, ctx) => {
        if (rowData.status !== 'PENDING') return null
        return <FetchFilesButton rowData={rowData} ctx={ctx} />
    }
}


class ProcessRowActionButtonCreator extends BaseRowActionButtonCreator {
    constructor() {
        super()
        this.actionId = 'process'
    }
    createWith = (rowData, ctx) => {
        if (rowData.status !== 'DOWNLOADED') return null
        return <ProcessButton rowData={rowData} ctx={ctx} />
    }
}


class DownloadFilesRowActionButtonCreator extends BaseRowActionButtonCreator {
    constructor() {
        super()
        this.actionId = 'downloadFiles'
    }
    createWith = (rowData, ctx) => {
        if (!['DOWNLOADED', 'DONE'].includes(rowData.status)) return null
        if (!rowData.files || rowData.files.length === 0) return null
        return <DownloadFilesButton rowData={rowData} />
    }
}


const FILE_SLOT_STYLES = {
    slot: {
        marginBottom: 12,
        padding: '10px 12px',
        borderRadius: 4,
        border: '1px solid #e0e0e0',
        background: '#fafafa',
    },
    slotPending: {
        border: '1px solid #ffe082',
        background: '#fffde7',
    },
    currentLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginBottom: 4,
    },
    dot: (color) => ({
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        marginRight: 6,
        flexShrink: 0,
    }),
    replacingLabel: {
        color: '#1976d2',
        marginTop: 2,
        marginBottom: 2,
        display: 'block',
    },
}

const EditFilesButton = ({ rowData, ctx }) => {
    const { axiosClient } = useAxiosClient()
    const [open, setOpen] = useState(false)
    const [driverFiles, setDriverFiles] = useState([])
    const [currentFiles, setCurrentFiles] = useState({})
    const [selectedFiles, setSelectedFiles] = useState({})
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(false)

    const handleOpen = async (event) => {
        event.stopPropagation()
        setFetching(true)
        try {
            const [driverFilesResp, requestFilesResp] = await Promise.all([
                axiosClient.get(`/billing/available-invoices/${rowData.pk}/driver-files/`),
                axiosClient.get(`/billing/available-invoices/${rowData.pk}/request-files/`),
            ])
            setDriverFiles(driverFilesResp.data.driver_files || [])
            const fileMap = {}
            for (const f of (requestFilesResp.data.files || [])) {
                if (f.driver_file_id != null) {
                    fileMap[f.driver_file_id] = f
                }
            }
            setCurrentFiles(fileMap)
            setSelectedFiles({})
            setOpen(true)
        } catch (error) {
            console.error('Error loading driver files:', error)
        } finally {
            setFetching(false)
        }
    }

    const handleClose = () => {
        setOpen(false)
        setDriverFiles([])
        setCurrentFiles({})
        setSelectedFiles({})
    }

    const handleFileChange = (event) => {
        const driverFileId = event.target.id
        const file = event.target.files[0]
        if (file) {
            setSelectedFiles(prev => ({ ...prev, [driverFileId]: file }))
        }
    }

    const handleSubmit = async () => {
        const entries = Object.entries(selectedFiles)
        if (entries.length === 0) return

        setLoading(true)
        try {
            const data = new FormData()
            const filesDesc = entries.map(([id]) => ({ id }))
            data.append('filesDesc', JSON.stringify(filesDesc))
            entries.forEach(([id, file]) => data.append(id, file))

            await axiosClient.post(`/billing/available-invoices/${rowData.pk}/request-files/`, data)
            handleClose()
            ctx.refreshTable()
        } catch (error) {
            console.error('Edit files error:', error)
        } finally {
            setLoading(false)
        }
    }

    const pendingSlots = driverFiles.filter(df => !currentFiles[df.id]).length

    if (fetching) return <CircularProgress size={20} />

    return (
        <>
            <IconButton size="small" onClick={handleOpen} title="Edit Files">
                <EditFilesIcon />
            </IconButton>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
                <DialogTitle>
                    Edit Files
                    {pendingSlots > 0 && (
                        <Typography variant="caption" style={{ marginLeft: 12, color: '#f57c00' }}>
                            {pendingSlots} slot{pendingSlots > 1 ? 's' : ''} without file
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {driverFiles.map(df => {
                        const current = currentFiles[df.id]
                        const selected = selectedFiles[String(df.id)]
                        const slotStyle = {
                            ...FILE_SLOT_STYLES.slot,
                            ...(current ? {} : FILE_SLOT_STYLES.slotPending),
                        }
                        return (
                            <div key={df.id} style={slotStyle}>
                                <div style={FILE_SLOT_STYLES.currentLabel}>
                                    <span style={FILE_SLOT_STYLES.dot(current ? '#4caf50' : '#ff9800')} />
                                    {current
                                        ? <Typography variant="caption" style={{ color: '#388e3c' }}>Current: {current.name}</Typography>
                                        : <Typography variant="caption" style={{ color: '#f57c00' }}>No file uploaded</Typography>
                                    }
                                </div>
                                {selected && (
                                    <Typography variant="caption" style={FILE_SLOT_STYLES.replacingLabel}>
                                        {current ? 'Replacing with: ' : 'Will upload: '}{selected.name}
                                    </Typography>
                                )}
                                <FileInput
                                    label={df.name}
                                    id={String(df.id)}
                                    extension={df.extension}
                                    onChangeFunc={handleFileChange}
                                    styles={{ padding: 0, width: '100%' }}
                                />
                            </div>
                        )
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || Object.keys(selectedFiles).length === 0}
                        color="primary"
                    >
                        {loading ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


class EditFilesRowActionButtonCreator extends BaseRowActionButtonCreator {
    constructor() {
        super()
        this.actionId = 'editFiles'
    }
    createWith = (rowData, ctx) => {
        if (rowData.status !== 'DOWNLOADED') return null
        return <EditFilesButton rowData={rowData} ctx={ctx} />
    }
}


export { FetchFilesRowActionButtonCreator, ProcessRowActionButtonCreator, DownloadFilesRowActionButtonCreator, EditFilesRowActionButtonCreator }
